import { Icon, type IconName } from '../ui/Icon';
import { StatusDot } from '../ui/StatusDot';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

interface NavItem {
  id: string;
  label: string;
  icon: IconName;
  badge?: number;
  superOnly?: boolean;
}

const NAV_MAIN: NavItem[] = [
  { id: 'dashboard', label: 'Início', icon: 'dashboard' },
  { id: 'files', label: 'Arquivos', icon: 'image' },
  { id: 'logs', label: 'Logs', icon: 'activity' }
];

const NAV_ADMIN: NavItem[] = [
  { id: 'companies', label: 'Empresas', icon: 'building', superOnly: true },
  { id: 'providers', label: 'Providers', icon: 'plug', superOnly: true },
  { id: 'templates', label: 'Templates', icon: 'sparkles' },
  { id: 'docs', label: 'Documentação', icon: 'book' }
];

interface SidebarProps {
  area: string;
  onArea: (id: string) => void;
  pendentes?: number;
  papel?: 'super_admin' | 'partner_admin';
}

export function Sidebar({ area, onArea, pendentes = 0, papel = 'super_admin' }: SidebarProps) {
  const isSuper = papel === 'super_admin';
  const adminItems = NAV_ADMIN.filter((n) => !n.superOnly || isSuper);
  const renderItem = (n: NavItem) => {
    const active = area === n.id;
    const badge = n.id === 'files' ? pendentes : (n.badge ?? 0);
    return (
      <button
        key={n.id}
        onClick={() => onArea(n.id)}
        aria-current={active ? 'page' : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          width: '100%',
          padding: '8px 10px',
          marginBottom: 2,
          fontSize: 13.5,
          fontWeight: active ? 600 : 500,
          borderRadius: 'var(--r-sm)',
          border: 'none',
          textAlign: 'left',
          background: active ? 'var(--brand-tint)' : 'transparent',
          color: active ? 'var(--brand-strong)' : 'var(--text-1)',
          transition: 'background 0.12s',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          if (!active) e.currentTarget.style.background = 'var(--bg-2)';
        }}
        onMouseLeave={(e) => {
          if (!active) e.currentTarget.style.background = 'transparent';
        }}
      >
        <Icon
          name={n.icon}
          size={17}
          style={{ color: active ? 'var(--brand)' : 'var(--text-2)' }}
        />
        <span style={{ flex: 1 }}>{n.label}</span>
        {badge > 0 && (
          <span
            className="tnum"
            style={{
              fontSize: 11,
              fontWeight: 700,
              minWidth: 20,
              height: 18,
              padding: '0 6px',
              borderRadius: 99,
              background: 'var(--amber)',
              color: 'oklch(0.28 0.04 70)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <aside
      style={{
        width: 'var(--sidebar-w)',
        flexShrink: 0,
        borderRight: '1px solid var(--border)',
        background: 'var(--sidebar-bg)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto'
      }}
    >
      <nav style={{ padding: '16px 12px 8px' }}>
        <div className="eyebrow" style={{ padding: '0 10px 8px' }}>Console</div>
        {NAV_MAIN.map(renderItem)}
      </nav>

      <nav style={{ padding: '8px 12px 16px' }}>
        <div className="eyebrow" style={{ padding: '8px 10px 8px' }}>
          {isSuper ? 'Administração' : 'Recursos'}
        </div>
        {adminItems.map(renderItem)}
      </nav>

      <div style={{ marginTop: 'auto', padding: 12, borderTop: '1px solid var(--border-soft)' }}>
        <WorkspaceSwitcher />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '8px 10px',
            borderRadius: 'var(--r-sm)',
            background: 'var(--bg-2)'
          }}
        >
          <StatusDot color="var(--success)" pulse />
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>Sistema operacional</div>
            <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>
              plataforma interna · v0.1
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
